import DOMPurify from 'dompurify';

/**
 * Sanitize untrusted HTML before it is injected into our DOM via
 * `dangerouslySetInnerHTML` / html-react-parser.
 *
 * Used for anything we don't author ourselves: inbound email bodies (and the
 * quoted thread carried into a reply/forward), and stored task / feed HTML.
 * Strips <script>, inline event handlers (onclick…), javascript:/vbscript:
 * URIs, and the <iframe>/<object>/<embed>/<form>/<base>/<meta>/<link> tags
 * that enable script execution, clickjacking, or page hijack — while keeping
 * the formatting tags and (DOMPurify-sanitized) inline styles real emails use.
 *
 * Defense-in-depth: callers rendering fully attacker-controlled HTML (e.g. raw
 * inbound email) should ALSO render it inside a sandboxed <iframe> so a sanitizer
 * bypass still can't touch the parent page, cookies, or Firebase session.
 */
export interface SanitizeOptions {
  /** Also drop <style> elements. Use this for HTML injected into OUR OWN document
   *  (a <style> block there restyles the whole app — UI redress); leave it off for
   *  HTML confined to a sandboxed iframe, where the stylesheet can only ever affect
   *  the email itself. */
  noStyleTags?: boolean;
}

const FORBIDDEN_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'base', 'meta', 'link'];

export function sanitizeHtml(dirty: string | null | undefined, opts: SanitizeOptions = {}): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(String(dirty), {
    USE_PROFILES: { html: true },
    FORBID_TAGS: opts.noStyleTags ? [...FORBIDDEN_TAGS, 'style'] : FORBIDDEN_TAGS,
    // Open links in a new tab safely; rel is added by the hook below.
    ADD_ATTR: ['target'],
  });
}

/**
 * Escape TEXT for interpolation into an HTML string — text nodes and quoted
 * attribute values alike. Plain-text email bodies / snippets are attacker-supplied
 * too: dropped into a template unescaped, `<img src=x onerror=…>` in a text/plain
 * message becomes live markup.
 */
export function escapeHtml(text: string | null | undefined): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Force target=_blank links to carry rel="noopener noreferrer" so a sanitized
// link can't reach back into window.opener.
if (typeof window !== 'undefined' && (DOMPurify as any).addHook) {
  DOMPurify.addHook('afterSanitizeAttributes', (node: any) => {
    if (node.tagName === 'A' && node.getAttribute && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

export default sanitizeHtml;
